import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ArtifactGallery = ({ artifacts }) => {
  const [selectedArtifact, setSelectedArtifact] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');

  const filters = [
    { id: 'all', label: 'All', icon: 'Grid3X3' },
    { id: 'screenshots', label: 'Screenshots', icon: 'Camera' },
    { id: 'logs', label: 'Logs', icon: 'FileText' },
    { id: 'reports', label: 'Reports', icon: 'BarChart3' }
  ];

  const filteredArtifacts = activeFilter === 'all' 
    ? artifacts 
    : artifacts?.filter(artifact => artifact?.type === activeFilter);

  const getArtifactIcon = (type) => {
    switch (type) {
      case 'screenshots':
        return 'Camera';
      case 'logs':
        return 'FileText';
      case 'reports':
        return 'BarChart3';
      default:
        return 'File';
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i))?.toFixed(2)) + ' ' + sizes?.[i];
  };

  const handleDownload = (artifact) => {
    // Mock download functionality
    const link = document.createElement('a');
    link.href = artifact?.url;
    link.download = artifact?.filename;
    document.body?.appendChild(link);
    link?.click();
    document.body?.removeChild(link);
  };

  return (
    <div className="bg-card rounded-lg p-6 border border-subtle">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Icon name="FolderOpen" size={24} color="var(--color-primary)" />
          <h2 className="text-xl font-semibold text-foreground">Execution Artifacts</h2>
        </div>
        <div className="text-sm text-muted-foreground">
          {filteredArtifacts?.length} items
        </div>
      </div>
      {/* Filter Tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        {filters?.map((filter) => (
          <button
            key={filter?.id}
            onClick={() => setActiveFilter(filter?.id)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeFilter === filter?.id
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
            }`}
          >
            <Icon name={filter?.icon} size={16} />
            {filter?.label}
          </button>
        ))}
      </div>
      {/* Artifacts Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {filteredArtifacts?.map((artifact, index) => (
          <div key={index} className="border border-subtle rounded-lg overflow-hidden hover:shadow-elevation-1 transition-shadow">
            {/* Preview */}
            <div className="aspect-video bg-muted/50 flex items-center justify-center relative group">
              {artifact?.type === 'screenshots' ? (
                <Image
                  src={artifact?.preview || artifact?.url}
                  alt={artifact?.filename}
                  className="w-full h-full object-cover"
                />
              ) : (
                <Icon 
                  name={getArtifactIcon(artifact?.type)} 
                  size={32} 
                  color="var(--color-muted-foreground)" 
                />
              )}
              
              {/* Overlay Actions */}
              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  iconName="Eye"
                  onClick={() => setSelectedArtifact(artifact)}
                >
                  View
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  iconName="Download"
                  onClick={() => handleDownload(artifact)}
                >
                  Download
                </Button>
              </div>
            </div>

            {/* Info */}
            <div className="p-3">
              <h4 className="font-medium text-foreground text-sm mb-1 truncate">
                {artifact?.filename}
              </h4>
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>{formatFileSize(artifact?.size)}</span>
                <span>{artifact?.timestamp}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
      {filteredArtifacts?.length === 0 && (
        <div className="text-center py-12 text-muted-foreground">
          <Icon name="FolderX" size={48} className="mx-auto mb-3 opacity-50" />
          <p>No artifacts found for the selected filter.</p>
        </div>
      )}
      {/* Artifact Viewer Modal */}
      {selectedArtifact && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg max-w-4xl w-full max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between p-6 border-b border-subtle">
              <div className="flex items-center gap-3">
                <Icon name={getArtifactIcon(selectedArtifact?.type)} size={20} />
                <h3 className="text-lg font-semibold text-foreground">
                  {selectedArtifact?.filename}
                </h3>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Download"
                  onClick={() => handleDownload(selectedArtifact)}
                >
                  Download
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setSelectedArtifact(null)}
                >
                  <Icon name="X" size={20} />
                </Button>
              </div>
            </div>
            
            <div className="p-6">
              {selectedArtifact?.type === 'screenshots' ? (
                <div className="text-center">
                  <Image
                    src={selectedArtifact?.url}
                    alt={selectedArtifact?.filename}
                    className="max-w-full h-auto rounded-lg border border-subtle"
                  />
                </div>
              ) : selectedArtifact?.type === 'logs' ? (
                <div className="bg-muted/50 rounded-lg p-4 max-h-96 overflow-auto">
                  <pre className="text-sm text-code whitespace-pre-wrap">
                    {selectedArtifact?.content}
                  </pre>
                </div>
              ) : (
                <div className="text-center py-12">
                  <Icon name="FileText" size={64} className="mx-auto mb-4 opacity-50" />
                  <p className="text-muted-foreground">
                    Preview not available for this file type.
                  </p>
                  <Button
                    variant="outline"
                    className="mt-4"
                    iconName="Download"
                    onClick={() => handleDownload(selectedArtifact)}
                  >
                    Download to View
                  </Button>
                </div>
              )}
              
              {/* File Info */}
              <div className="mt-6 pt-6 border-t border-subtle">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Size:</span>
                    <div className="font-medium text-foreground">
                      {formatFileSize(selectedArtifact?.size)}
                    </div>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Type:</span>
                    <div className="font-medium text-foreground capitalize">
                      {selectedArtifact?.type}
                    </div>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Created:</span>
                    <div className="font-medium text-foreground">
                      {selectedArtifact?.timestamp}
                    </div>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Format:</span>
                    <div className="font-medium text-foreground">
                      {selectedArtifact?.format}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArtifactGallery;